import { useState, useEffect } from "react";
import axios from "axios";
import {
  Pagination,
  Select,
  Rate,
  Progress,
  Typography,
  message,
  Col,
  Row,
} from "antd";
import ReviewCard from "./ReviewCard"; // Assuming you have a ReviewCard component
import { apiEndpoint } from "../../constants/constants";

const { Option } = Select;
const { Text } = Typography;

const ReviewList = ({ product }) => {
  const [reviews, setReviews] = useState([]);
  const [totalReviews, setTotalReviews] = useState(0);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("date_desc"); // Default sort by date descending
  const [selectedRating, setSelectedRating] = useState(null); // Default: View All Reviews
  const [ratingBreakdown, setRatingBreakdown] = useState([]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${apiEndpoint}/review`, {
          params: {
            product,
            rating: selectedRating, // Send the selected rating to backend
            sortBy,
            page,
            limit: 10,
          },
        });
        setReviews(response.data.reviews);
        setTotalReviews(response.data.totalReviews);
        setRatingBreakdown(response.data.ratingBreakdown); // Set the breakdown of reviews
      } catch (error) {
        message.error("Failed to fetch reviews. Please try again.");
        console.error("Failed to fetch reviews:", error);
      }
    };

    fetchReviews();
  }, [product, selectedRating, sortBy, page]);

  // Handlers for filter changes
  const handleRatingFilterChange = (value) => {
    setSelectedRating(value);
    setPage(1); // Reset to first page when filter changes
  };

  const handleSortChange = (value) => {
    setSortBy(value);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Calculate total reviews for progress percentage
  const totalRatings = ratingBreakdown.reduce(
    (sum, item) => sum + item.count,
    0,
  );

  const totalPages = Math.ceil(totalRatings / 10);

  return (
    <div style={{ marginBottom: "24px" }}>
      {/* Rating Breakdown */}
      <div style={{ marginBottom: "20px" }}>
        <Typography.Title level={4}>Reviews</Typography.Title>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {/* All Reviews Row */}
          <div
            style={{
              cursor: "pointer",
              padding: "10px 14px",
              borderRadius: "6px",
              backgroundColor:
                selectedRating === null ? "#e6f7ff" : "transparent",
              border:
                selectedRating === null
                  ? "1px solid #1890ff"
                  : "1px solid #d9d9d9",
              transition: "all 0.2s",
            }}
            onClick={() => handleRatingFilterChange(null)}
          >
            <Row align="middle" gutter={[8, 8]}>
              {/* Mobile: Full Row (24) | Desktop: Single Row Span (5) */}
              <Col xs={24} sm={5}>
                <Text strong style={{ fontSize: "14px" }}>
                  All reviews
                </Text>
              </Col>

              {/* Mobile: Full Row (24) | Desktop: Single Row Span (14) */}
              <Col xs={24} sm={14}>
                <Progress percent={100} showInfo={false} />
              </Col>

              {/* Mobile: Full Row (24) | Desktop: Single Row Span (5) */}
              <Col xs={24} sm={5} style={{ textAlign: "left" }}>
                <Text type="secondary" style={{ fontSize: "13px" }}>
                  {totalRatings} reviews
                </Text>
              </Col>
            </Row>
          </div>

          {/* Star Rating Rows */}
          {[5, 4, 3, 2, 1].map((star) => {
            const count =
              ratingBreakdown.find((item) => item.rating === star)?.count || 0;
            const percentage = totalRatings ? (count / totalRatings) * 100 : 0;
            const isSelected = selectedRating === star;

            return (
              <div
                key={star}
                style={{
                  cursor: "pointer",
                  padding: "10px 14px",
                  borderRadius: "6px",
                  backgroundColor: isSelected ? "#e6f7ff" : "transparent",
                  border: isSelected
                    ? "1px solid #1890ff"
                    : "1px solid #d9d9d9",
                  transition: "all 0.2s",
                }}
                onClick={() => handleRatingFilterChange(star)}
              >
                <Row align="middle" gutter={[8, 8]}>
                  {/* 1. Star Rating Display */}
                  <Col xs={24} sm={5}>
                    <Rate disabled value={star} style={{ fontSize: "14px" }} />
                  </Col>

                  {/* 2. Progress Bar */}
                  <Col xs={24} sm={14}>
                    <Progress percent={percentage} showInfo={false} />
                  </Col>

                  {/* 3. Count Text */}
                  <Col xs={24} sm={5} style={{ textAlign: "left" }}>
                    <Text type="secondary" style={{ fontSize: "13px" }}>
                      {count} reviews
                    </Text>
                  </Col>
                </Row>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sorting Options */}
      <Col xs={20} sm={10} md={5}>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "20px",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <label style={{ fontWeight: 500 }}>Sort by:</label>
          <Select
            value={sortBy}
            onChange={handleSortChange}
            style={{ width: "100%" }}
          >
            <Option value="date_desc">Newest First</Option>
            <Option value="date_asc">Oldest First</Option>
            <Option value="rating_desc">Highest Rated First</Option>
            <Option value="rating_asc">Lowest Rated First</Option>
          </Select>
        </div>
      </Col>

      {/* Reviews */}
      <div>
        {reviews.length > 0 ? (
          reviews.map((review) => (
            <ReviewCard key={review._id} review={review} />
          ))
        ) : (
          <Text type="secondary">No reviews available.</Text>
        )}
      </div>

      {/* Pagination */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "8px",
          marginTop: "32px",
        }}
      >
        <Pagination
          current={page}
          total={totalReviews}
          pageSize={10}
          onChange={handlePageChange}
        />
        {totalReviews > 0 && (
          <Text type="secondary" style={{ fontSize: "14px" }}>
            Page {page} of {totalPages}
          </Text>
        )}
      </div>
    </div>
  );
};

export default ReviewList;
